import type {
  CustomerContactApiItem,
  CustomerSitePhoneConfigApiItem,
} from '~/services/api/SiteApi'

export interface CustomerPhoneItem {
  title: string
  phone?: string
  dialPhone?: string
  desc?: string
  buttonDesc?: string
  configType: number
  icon?: string
}

interface ApiListContainer<T> {
  body?: T[] | ApiListContainer<T>
  list?: T[]
  contactList?: T[]
  contactItemList?: T[]
  configList?: T[]
  items?: T[]
  site?: string
}

type ApiListResponse<T> = T[] | ApiListContainer<T> | null | undefined

function extractList<T>(response: ApiListResponse<T>): T[] {
  if (Array.isArray(response)) { return response }
  if (!response) { return [] }
  const directList = response.list
    || response.contactList
    || response.contactItemList
    || response.configList
    || response.items
    || []
  if (Array.isArray(directList) && directList.length) { return directList }
  return Array.isArray(response.body) ? response.body : extractList(response.body)
}

export function getCustomerContactSite(
  response: ApiListResponse<CustomerContactApiItem>,
) {
  if (!response || Array.isArray(response)) { return '' }
  if (response.site) { return response.site }
  return !Array.isArray(response.body) ? (response.body?.site || '') : ''
}

function getContactType(item: CustomerContactApiItem) {
  const rawType = item.contactType ?? item.configType ?? item.type ?? 0
  const textType = String(rawType).toLowerCase()
  if (textType.includes('voip') || textType.includes('internet')) { return 1 }
  const numberType = Number(rawType)
  return Number.isFinite(numberType) ? numberType : 0
}

export function normalizeCustomerContacts(
  response: ApiListResponse<CustomerContactApiItem>,
  callText: string,
): CustomerPhoneItem[] {
  return extractList(response).map(item => ({
    title: item.title || '',
    phone: item.displayPhone || item.phone || '',
    dialPhone: item.dialPhone || '',
    desc: item.desc || '',
    buttonDesc: item.buttonDesc || callText,
    configType: getContactType(item),
    icon: item.iconUrl || item.icon || '',
  })).filter(item => item.title || item.phone || item.configType === 1)
}

function getSitePhoneItems(config: CustomerSitePhoneConfigApiItem) {
  if (Array.isArray(config.phoneItemList)) { return config.phoneItemList }
  return Array.isArray(config.phoneList) ? config.phoneList : []
}

export function normalizeSitePhoneConfigs(
  response: ApiListResponse<CustomerSitePhoneConfigApiItem>,
) {
  return extractList(response).map(config => ({
    ...config,
    site: config.site?.trim() || '',
    siteName: config.siteName || config.site || '',
    phoneItemList: getSitePhoneItems(config)
      .filter(item => item.title || item.displayPhone || item.dialPhone),
  })).filter(config => config.site && config.phoneItemList.length)
}

function normalizeSiteCode(site?: string) {
  return site?.trim().toUpperCase() || ''
}

export function getCurrentSitePhoneConfig(
  configs: ReturnType<typeof normalizeSitePhoneConfigs>,
  currentSite?: string,
) {
  const siteCode = normalizeSiteCode(currentSite)
  if (!siteCode) { return undefined }
  return configs.find(config => normalizeSiteCode(config.site) === siteCode)
}

export function getOtherSitePhoneConfigs(
  configs: ReturnType<typeof normalizeSitePhoneConfigs>,
  currentSite?: string,
) {
  const siteCode = normalizeSiteCode(currentSite)
  if (!siteCode) { return [] }
  return configs.filter(config => normalizeSiteCode(config.site) !== siteCode)
}
